import { DateTime, Duration } from "luxon";

import { serverNow } from "@storyboard/web-client";
import { downloadOldestFeedItemEvents } from "@/models/actions/bootstrap";
import { enqueueCalculateUnreads } from "@/models/actions/calculateUnreads";
import { downloadFeedItemContent } from "@/models/actions/initialFeedLoad";
import { loadWorkspaceWorkflowItems } from "@/models/actions/workflows";
import { initializeFromAlias, initializeIsAliasFeedFromIds } from "@/models/feedAliasMetadata";
import type { PreferredLanguage } from "@/models/languages";
import { playSoundForNewInboundItems } from "@/models/newMessageSound";
import { fetchParentIds } from "@/models/repositories/Item";
import { getContentCursor, setContentCursor } from "@/models/sync/contentCursor";
import { cursorFor } from "@/models/sync/cursorStore";
import { partitionContentFetch, selectEagerContentItemIds } from "@/models/sync/eagerContent";
import type { SyncContext } from "@/models/sync/syncTargets";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Item } from "@/store/types";

/**
 * How far back a resync looks for items whose content may still be arriving. The pipeline finishes a message's
 * transcription, translations and encodings within a few minutes of its creation.
 */
export const RECENT_CONTENT_WINDOW_MS = Duration.fromObject({ minutes: 10 }).toMillis();

/** What one table walk wrote, so the post-fetch pass can act on it. */
export type SyncDelta = {
  /** Feeds named by the rows that landed; the unread recompute is scoped to these. */
  feedIds: Set<string>;
  /** Items that landed; the newest new ones get their content fetched before the pass ends. */
  itemIds: Set<string>;
  /** The item rows as the upsert returned them, `firstInsert` included, for the new-message sound. */
  upsertedItems: Item[];
  /** Feeds whose alias metadata may have changed: feed, permission and command-alias rows name them. */
  aliasFeedIds: Set<string>;
};

/** Per-item reads the signals asked for on top of what the walk itself returned. */
export type SyncExtras = {
  /** Items named by `events` rows, whose read receipts are re-fetched. */
  eventItemIds: Set<string>;
  /** Content ids named by artifact rows, whose parent items get their content re-fetched. */
  contentIds: Set<string>;
};

export function emptySyncDelta(): SyncDelta {
  return { feedIds: new Set(), itemIds: new Set(), upsertedItems: [], aliasFeedIds: new Set() };
}

/** Mirrors `UseStorage().getLocalStorage("preferredLanguage")` for this non-React caller. */
function readPreferredLanguage(): PreferredLanguage | undefined {
  try {
    const raw = window.localStorage.getItem("preferredLanguage");
    return raw ? (JSON.parse(raw) as PreferredLanguage) : undefined;
  } catch {
    return undefined;
  }
}

/** Whether any of the content ids belongs to a workflow item rather than a feed item. */
function namesWorkflowContent(contentIds: Set<string>): boolean {
  if (contentIds.size === 0) return false;
  for (const workflowItem of store.getAll("workflowItem").values()) {
    if (contentIds.has(workflowItem.contentId)) return true;
  }
  return false;
}

/**
 * Content ids of items created inside the recent window whose content was fetched whole at least once. A resync hands
 * these to the content pass so artifacts that landed while no signal could reach the tab are picked up as a delta.
 */
export function recentlyCreatedContentIds(now: Date = serverNow()): string[] {
  const floor = DateTime.fromJSDate(now).minus({ milliseconds: RECENT_CONTENT_WINDOW_MS });
  const contentIds: string[] = [];
  for (const item of store.getAll("item").values()) {
    if (item.loadedContent && item.contentId && DateTime.fromJSDate(new Date(item.createdAt)) >= floor) {
      contentIds.push(item.contentId);
    }
  }
  return contentIds;
}

/**
 * The one post-fetch pass: eager content and receipts for the items that landed, then the side effects the wire-fed
 * ingest used to run.
 *
 * New items get their content whole. Items an artifact signal named get only the artifacts changed since this tab's
 * own content cursor, which is sound because the cursor only moved when this tab finished a pass over every signal it
 * held: an item fetched whole once is complete up to that pass, and no signal still queued here can predate it.
 * Artifacts that belong to a workflow item refresh the workflow tables instead, since `fetchParentIds` only knows feed
 * items. `firstInsert` comes from the upsert, so the sound and unread rules are the ones the old ingest applied.
 */
export async function applySyncDelta(delta: SyncDelta, ctx: SyncContext, extras: SyncExtras): Promise<void> {
  const requestStart = serverNow();
  const newItemIds = selectEagerContentItemIds(delta.upsertedItems.filter((item) => item.firstInsert));
  const signaledParentIds = extras.contentIds.size > 0 ? await fetchParentIds([...extras.contentIds]) : [];
  const { full, delta: sinceCursor } = partitionContentFetch(
    signaledParentIds,
    (itemId) => store.get("item", itemId)?.loadedContent === true,
  );
  const fullItemIds = [...new Set([...newItemIds, ...full])];
  const deltaItemIds = sinceCursor.filter((id) => !fullItemIds.includes(id));
  const contentCursor = deltaItemIds.length > 0 ? getContentCursor(ctx.workspaceId) : undefined;

  // `downloadFeedItemContent` also pulls the read receipts for its items, so only the rest need their own fetch. Items
  // the tab does not hold have no rows a receipt could decorate.
  const contentItemIds = new Set([...fullItemIds, ...deltaItemIds]);
  const receiptOnlyItemIds = [...extras.eventItemIds].filter((id) => !contentItemIds.has(id) && store.get("item", id));

  const language = readPreferredLanguage();
  await Promise.all([
    downloadFeedItemContent(ctx.workspaceId, fullItemIds, language),
    downloadFeedItemContent(ctx.workspaceId, deltaItemIds, language, contentCursor?.toISOString()),
    receiptOnlyItemIds.length > 0
      ? downloadOldestFeedItemEvents(receiptOnlyItemIds, ctx.workspaceId, undefined, true, 2)
      : Promise.resolve(),
    namesWorkflowContent(extras.contentIds)
      ? loadWorkspaceWorkflowItems({ workspaceId: ctx.workspaceId, myAccountId: ctx.accountId })
      : Promise.resolve(),
  ]);
  if (contentItemIds.size > 0) {
    setContentCursor(ctx.workspaceId, cursorFor(requestStart));
  }

  if (delta.aliasFeedIds.size > 0) {
    await initializeIsAliasFeedFromIds([...delta.aliasFeedIds]);
    // Items stored before these feeds' aliases landed were derived as not the driver's; re-derive them.
    await initializeFromAlias(delta.aliasFeedIds);
  }

  playSoundForNewInboundItems(delta.upsertedItems, ctx.accountId);

  // Receipts that landed for signaled items clear unreads too, so their feeds join the recompute.
  const feedIds = new Set(delta.feedIds);
  for (const itemId of extras.eventItemIds) {
    const feedId = store.get("item", itemId)?.feedId;
    if (feedId) feedIds.add(feedId);
  }
  if (feedIds.size === 0) return;
  const membership = rowsByIndex("workspaceMembership", idx.membershipsByAccount, ctx.accountId).find(
    (row) => row.workspaceId === ctx.workspaceId,
  );
  if (!membership) return;
  enqueueCalculateUnreads({ membership, feedIds: [...feedIds], alsoSetAsRead: true });
}
